import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";
import { usePageMeta } from "@/hooks/usePageMeta";
import VideoSection from "@/components/VideoSection";

const MEDIA = {
  miroir: {
    hero: "/images/miroir-hero.webp",
    gallery: [
      "/images/miroir-1.webp", "/images/miroir-2.webp", "/images/miroir-3.webp", "/images/miroir-4.webp",
    ],
  },
  givre: {
    hero: "/images/givre-hero.webp",
    gallery: [
      "/images/givre-1.webp", "/images/givre-2.webp", "/images/givre-3.webp",
    ],
  },
  securite: {
    hero: "/images/securite-commerce.webp",
    gallery: [],
  },
};

export default function FilmDetail({ filmKey }) {
  const { t } = useLanguage();
  usePageMeta(filmKey);
  const d = t(`films.${filmKey}Page`);
  const m = MEDIA[filmKey];

  return (
    <div className="animate-fade-up">
      {/* HERO, editorial split */}
      <section className="py-20 sm:py-28 lg:py-40 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <Link
              to="/pellicules"
              data-testid="film-back-link"
              className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              {t("films.backTo")}
            </Link>
            <div className="text-xs tracking-[0.3em] uppercase font-display font-semibold text-blue-600">{d.eyebrow}</div>
            <h1 data-testid="film-title" className="font-heading text-5xl lg:text-6xl font-normal tracking-tight leading-[1.15] text-slate-900 mt-4">
              {d.title}
            </h1>
            <p className="mt-8 text-lg text-slate-600 leading-relaxed font-normal">
              {d.intro}
            </p>
            <Link to="/contact" data-testid="film-hero-cta" className="block sm:inline-block">
              <Button className="w-full sm:w-auto mt-10 rounded-full bg-blue-600 hover:bg-blue-700 text-white h-auto min-h-14 px-6 py-3.5 text-base font-medium shadow-lg shadow-black/10 whitespace-normal text-center leading-snug">
                {t("films.ctaButton")}
                <ArrowRight className="ml-2 w-4 h-4 flex-shrink-0" />
              </Button>
            </Link>
          </div>
          <div className="rounded-2xl overflow-hidden aspect-[4/3] bg-slate-100 shadow-[0_20px_50px_rgba(0,0,0,0.06)]">
            <img src={m.hero} alt={d.title} className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* VIDÉO, page miroir uniquement, sous la description du produit */}
      {filmKey === "miroir" && <VideoSection testId="film-video" />}

      {/* BENEFITS */}
      <section data-testid="film-benefits" className="py-20 sm:py-28 lg:py-40 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-xs tracking-[0.3em] uppercase font-display font-semibold text-blue-600 mb-12">
            {t("films.benefitsEyebrow")}
          </div>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-8">
            {d.benefits.map((b, i) => (
              <div key={b} data-testid={`film-benefit-${i}`} className="flex items-start gap-4">
                <span className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 grid place-items-center flex-shrink-0 mt-0.5">
                  <Check className="w-4 h-4" strokeWidth={2.6} />
                </span>
                <span className="text-slate-800 leading-relaxed font-normal text-lg">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY — affichée seulement s'il y a de vraies photos */}
      {m.gallery.length > 0 && (
      <section data-testid="film-gallery" className="py-20 sm:py-28 lg:py-40 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-xs tracking-[0.3em] uppercase font-display font-semibold text-blue-600 mb-12">
            {t("films.galleryEyebrow")}
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            {m.gallery.map((src, i) => (
              <figure
                key={src}
                data-testid={`film-gallery-item-${i}`}
                className="aspect-square overflow-hidden rounded-2xl bg-slate-100"
              >
                <img
                  src={src}
                  alt={`${d.eyebrow}, ${t("films.thumbAlt")} ${i + 1}`}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                />
              </figure>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* IDEAL FOR + NOTE */}
      <section className="py-20 sm:py-28 lg:py-40 bg-white">
        <div className={`max-w-5xl mx-auto px-6 lg:px-8 grid gap-16 ${d.note ? "lg:grid-cols-2" : ""}`}>
          <div>
            <div className="text-xs tracking-[0.3em] uppercase font-display font-semibold text-blue-600 mb-8">
              {t("films.idealEyebrow")}
            </div>
            <ul className="space-y-4">
              {d.ideal.map((item) => (
                <li key={item} className="flex items-center gap-3 text-slate-800 font-normal text-lg">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          {d.note && (
          <div className="rounded-2xl bg-slate-50 border-l-2 border-blue-400 p-8 lg:p-10 self-start">
            <div className="text-xs tracking-[0.3em] uppercase font-display font-semibold text-blue-600 mb-4">
              {t("films.noteEyebrow")}
            </div>
            <p className="text-slate-600 leading-relaxed font-normal">{d.note}</p>
          </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="pb-20 sm:pb-28 lg:pb-40 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="rounded-3xl bg-slate-950 text-white p-12 lg:p-20 relative overflow-hidden bg-grid">
            <div className="absolute -top-24 -right-24 w-96 h-96 bg-blue-600/30 rounded-full blur-3xl" />
            <div className="relative">
              <div className="text-xs tracking-[0.3em] uppercase font-display font-semibold text-blue-400">{t("films.ctaEyebrow")}</div>
              <h2 className="font-heading text-3xl lg:text-5xl font-normal tracking-tight leading-[1.25] mt-3 max-w-3xl">
                {t("films.ctaTitle")}
              </h2>
              <p className="mt-6 text-slate-300 max-w-2xl text-lg font-normal">
                {t("films.ctaText")}
              </p>
              <Link to="/contact" data-testid="film-cta-button" className="block sm:inline-block">
                <Button className="w-full sm:w-auto mt-10 rounded-full bg-blue-600 hover:bg-blue-700 text-white h-auto min-h-14 px-6 py-3.5 text-base font-medium shadow-lg shadow-black/10 whitespace-normal text-center leading-snug">
                  {t("films.ctaButton")}
                  <ArrowRight className="ml-2 w-4 h-4 flex-shrink-0" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
